
import React from "react";
import { Grid } from "@mui/material";

import Chart from "react-apexcharts";

import {
  parseDatesArray,
  formatFloat,
  formatSeriesData,
  capitalizeString,
  formatPieSeriesData
} from "./ChartFunctions"

const ChartGeneralOptions = {
  dataLabels: {
    enabled: false,
  },
  grid: {
    show: true,
    showForSingleSeries: true,
    borderColor: "#D1CEC5", // color of the gridlines
    strokeDashArray: 3, // length of dashes in pixels
    position: "back", // place the gridlines behind the series
    xaxis: {
      lines: {
        show: false, // show/hide gridlines for x-axis
      },
    },
    yaxis: {
      lines: {
        show: true, // show/hide gridlines for y-axis
      },
    },
  },
};

const AxisLabels = {
  labels: {
    style: {
      color: "#626364",
      fontFamily: '"MADE TOMMY", sans-serif',
      fontSize: "12px",
      fontWeight: 400,
      lineHeight: "15px",
      letterSpacing: "0px",
      textAlign: "left",
    },
  },
};

const plotOptions = (type, label) => {
  if (type === "bar")
    return {
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: "50%",
        },
      },
    };

  if (type === "donut")
    return {
      plotOptions: {
        pie: {
          donut: {
            size: "50%",
            labels: {
              show: true,
              name: {
                show: true,
                fontSize: "12px",
                fontFamily: '"MADE TOMMY", sans-serif',
                fontWeight: 400,
                color: "#626364",
                lineHeight: "15px",
                letterSpacing: "0px",
                textAlign: "center",
              },
              value: {
                show: true,
                fontSize: "24px",
                fontFamily: '"MADE TOMMY", sans-serif',
                fontWeight: 500,
                color: "#626364",
                margin: "0 !important",
                lineheight: "32px",
                letterSpacing: "0px",
                textAlign: "center",
                formatter: function (val) {
                  if (label?.length > 3) return `${formatFloat(val)}`;
                  else return `${formatFloat(val)} ${capitalizeString(label)}`;
                },
              },
            },
          },
        },
      },
    };
};

const CustomChart = ({
  chartData,
  chartType,
  chartColors,
  isStacked,
  chartDescription,
  chartExportFileName,
  xLabel,
  yLabel,
  legend,
  groupChart = false,
}) => {

  if (
    chartType === "donut"
    && xLabel?.categories.length !== chartColors
  ) {
    console.warn(`The colors and categories lists are not the same length for the chart ${chartExportFileName}. This may result in unexpected behaviour.`)
  }

  const seriesData = chartType === "donut"
  ? (
    Object.values(chartData).length > 1 ?
    formatPieSeriesData(chartData,xLabel)
    : Object.values(chartData)[0].data
  ) : formatSeriesData(chartData ?? {})

  const customLineChartColors = [
    "#164A1A",
    "#D36BD7",
    "#83DA38",
    "#EF7709",
    "#C00829",
    "#0A869A",
    "#919397",
    "#062308",
  ];

  const customPieChartColors = [...new Set(chartColors)].length === seriesData.length ? chartColors : 
  [
    "#0A869A",
    "#C00829",
    "#D36BD7",
    "#EF7709",
    "#83DA38",
    "#164A1A",
    "#062308",
    "#919397",
  ];

  const ChartData = {
    series: seriesData,
    type: chartType,
    height: 480,
    options: {
      ...ChartGeneralOptions,
      chart: {
        ...(isStacked && {stacked: true }),
        toolbar: {
          show: true,
          export: {
            csv: {
              filename: chartExportFileName,
              valueFormatter(y) {
                return `${formatFloat(y)} ${
                  yLabel?.includes("normalized")
                    ? ""
                    : capitalizeString(yLabel)
                }`;
              }
            },
            svg: {
              filename: chartExportFileName
            },
            png: {
              filename: chartExportFileName
            },
          },
        },
      },
      grid: {
        ...ChartGeneralOptions.grid,
        xaxis: {
          lines: {
            show: chartType === "line",
          },
        },
        yaxis: {
          lines: {
            show: chartType !== "radar",
          },
        },
      },
      fill: {
        colors:
          chartType === "radar" || chartType === "donut"
            ? customPieChartColors
            : chartColors,
        ...(chartType === "radar" ? { opacity: 0.3 } : {}),
      },
      stroke: {
        curve: "smooth",
        width:
          chartType === "line" || chartType === "radar" || chartType === "donut"
            ? 2
            : 0,
        colors:
          chartType === "radar"
            ? customPieChartColors
            : chartType === "line"
            ? customLineChartColors
            : chartType === "donut"
            ? ["#FFF"]
            : chartColors,
      },
      markers: {
        ...(legend?.markers || {}), // Ensure markers exist
        size: chartType === "radar" ? 3 : [4, 4],
        colors:
          chartType === "radar" || chartType === "donut"
            ? customPieChartColors
            : chartType === "line"
            ? customLineChartColors
            : chartColors,
      },
      ...plotOptions(chartType, yLabel),
      colors:
        chartType === "donut" || chartType === "radar"
          ? customPieChartColors
          : chartType === "line"
          ? customLineChartColors
          : chartColors,
      xaxis: {
        ...AxisLabels,
        type: xLabel?.category_type || null,
        categories: xLabel?.categories
          ? parseDatesArray(xLabel?.categories)
          : [],
      },
      labels: xLabel?.categories || [],
      legend:
        chartType === "donut"
          ? {
              position: "left",
              offsetX: 10,
              onItemClick: {
                toggleDataSeries: false,
              },
              formatter: function (seriesName, opts) {
                const colors = customPieChartColors;
                const color = colors[opts.seriesIndex] || "#000";
                return `${seriesName} <br /> <span style='color: ${color};font-family: MADE TOMMY,"sans-serif" !important;font-size: 18px;font-weight: 500;line-height: 32px;letter-spacing: 0;'>${formatFloat(
                  seriesData[opts.seriesIndex]
                )} ${capitalizeString(yLabel)}</span>`;
              },
            }
          : {
              ...legend,
              markers: {
                size: 7,
              },
              ...(isStacked && {offsetX: -15}),
              showForSingleSeries: chartType !== "radar",
              onItemClick: {
                toggleDataSeries: false,
              },
            },
      yaxis: {
        labels: {
          ...AxisLabels,
          formatter: function (value) {
            return `${formatFloat(value)} ${
              yLabel?.includes("normalized")
                ? ""
                : capitalizeString(yLabel)
            }`;
          },
        },
      },
    },
  };

  return (
    <>
      <Grid
        item
        xs={12}
        display="flex"
        flex-direction="row"
        alignItems="flex-start"
      >
        {groupChart ? (
          <Grid item xs={12}>
            <Chart {...ChartData} />
          </Grid>
        ) : (
          <>
            <Grid item xs={chartDescription ? 9 : 12}>
              <Chart {...ChartData} />
            </Grid>
            {chartDescription && (
              <Grid item xs={3} sx={{ margin: "0rem 2rem" }}>
                {chartDescription && (
                  <div
                    style={{
                      fontFamily: '"MADE TOMMY", sans-serif',
                      fontWeight: 400,
                      color: "#626364",
                      marginRight: "10rem",
                      width: "100%",
                    }}
                  >
                    {chartDescription}
                  </div>
                )}
              </Grid>
            )}
          </>
        )}
      </Grid>
    </>
  );
};

export default CustomChart;
